import { useState, useEffect, useId, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  EyeIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline'

/* ================================================================
   COMPLIANCE YEARS
   ================================================================ */
const YEARS = [
  { id: '2025-26', label: '2025–26', period: '1 Jul 2025 – 30 Jun 2026', status: 'active' },
  { id: '2024-25', label: '2024–25', period: '1 Jul 2024 – 30 Jun 2025', status: 'locked', lockedDate: '15/07/2025' },
  { id: '2023-24', label: '2023–24', period: '1 Jul 2023 – 30 Jun 2024', status: 'locked', lockedDate: '10/07/2024' },
  { id: '2022-23', label: '2022–23', period: '1 Jul 2022 – 30 Jun 2023', status: 'locked', lockedDate: '05/07/2023' },
]

function deriveShortName(name, code) {
  if (code) return code
  if (name?.startsWith('TwinStars ')) return name.replace('TwinStars ', '').split(' ')[0]
  return name?.split(' ')[0] || name
}

/* ================================================================
   DIGITAL TARGET ROUTES — links to existing app pages
   ================================================================ */
const DIGITAL_ROUTES = {
  'Venue Risk Assessment': '/governance-safe/risk',
  'SMR Register': '/compliance-safe/smr-registry',
  'AUSTRAC Reporting': '/compliance-safe/smr-registry',
  'Training Register': '/governance-safe/training',
  'KYC Registry': '/compliance-safe/kyc-registry',
  'ECDD Register': '/compliance-safe/ecdd',
  'Alert Queue': '/monitor-safe/alerts',
  'Staff Management': '/admin/employees',
}

/* ================================================================
   STATUS HELPERS
   ================================================================ */
const STATUS_MAP = {
  done: { label: 'Done', bg: 'bg-green-100 dark:bg-green-900/30', tx: 'text-green-800 dark:text-green-200', icon: CheckCircleIcon },
  partial: { label: 'Partial', bg: 'bg-yellow-100 dark:bg-yellow-900/30', tx: 'text-yellow-800 dark:text-yellow-200', icon: ExclamationTriangleIcon },
  in_progress: { label: 'In Progress', bg: 'bg-violet-100 dark:bg-violet-900/30', tx: 'text-violet-800 dark:text-violet-200', icon: ClockIcon },
  not_done: { label: 'Not Done', bg: 'bg-red-100 dark:bg-red-900/30', tx: 'text-red-800 dark:text-red-200', icon: ClockIcon },
  not_required: { label: 'Not Required', bg: 'bg-slate-100 dark:bg-slate-800', tx: 'text-slate-600 dark:text-slate-400', icon: null },
}

function StatusDot({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.not_done
  const Icon = s.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.tx}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {s.label}
    </span>
  )
}

function getOverallVenueStatus(venueData) {
  if (venueData.every((v) => v.status === 'done')) return 'done'
  if (venueData.some((v) => v.status === 'not_done')) return 'partial'
  if (venueData.some((v) => v.status === 'partial')) return 'partial'
  return 'done'
}

/* ================================================================
   FILE CHIP — opens uploaded PDF via authenticated download
   ================================================================ */
function formatUploadDate(file) {
  const raw = file?.date || file?.uploaded_at || file?.uploadedAt
  if (!raw) return ''
  try {
    const d = new Date(raw)
    if (Number.isNaN(d.getTime())) return String(raw).slice(0, 10)
    return d.toLocaleDateString('en-AU')
  } catch {
    return String(raw).slice(0, 10)
  }
}

function FileChip({ file, isLocked, onRemove, obligationId, fileIndex, selectedYear, venueId }) {
  const [opening, setOpening] = useState(false)
  const [removing, setRemoving] = useState(false)
  const label = file?.name || 'document.pdf'
  const dateLabel = formatUploadDate(file)

  const openFile = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!obligationId || fileIndex == null || opening) return
    setOpening(true)
    try {
      const qs = new URLSearchParams()
      if (selectedYear) qs.set('year', selectedYear)
      if (venueId) qs.set('venueId', venueId)
      const url = getLocalApiUrl(
        `/api/governance/obligations/${encodeURIComponent(obligationId)}/files/${encodeURIComponent(String(fileIndex))}/download${qs.toString() ? `?${qs}` : ''}`
      )
      const res = await authFetch(url)
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error(j.error || `Open failed (${res.status})`)
      }
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const w = window.open(objectUrl, '_blank', 'noopener,noreferrer')
      if (!w) {
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = label
        a.click()
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    } catch (err) {
      console.error('Open obligation file failed:', err)
      alert(err.message || 'Could not open document')
    } finally {
      setOpening(false)
    }
  }

  const removeFile = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (isLocked || removing || obligationId == null || fileIndex == null) return
    if (!window.confirm(`Remove "${label}" from this obligation?`)) return
    setRemoving(true)
    try {
      if (onRemove) {
        await onRemove()
      }
    } catch (err) {
      console.error('Remove obligation file failed:', err)
      alert(err.message || 'Could not remove document')
    } finally {
      setRemoving(false)
    }
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs text-gray-700 dark:text-gray-300 hover:border-psg-teal/50 transition-colors">
      <button
        type="button"
        onClick={openFile}
        disabled={opening || obligationId == null}
        className="inline-flex items-center gap-1.5 no-underline text-inherit hover:text-psg-teal transition-colors disabled:opacity-50"
        title={opening ? 'Opening…' : 'View PDF'}
      >
        <DocumentTextIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
        <span className="max-w-[220px] truncate">{label}</span>
        {dateLabel ? <span className="text-gray-400 text-[10px]">{dateLabel}</span> : null}
        <EyeIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
      </button>
      {!isLocked && (
        <button
          type="button"
          onClick={removeFile}
          disabled={removing}
          className="p-0.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
          title="Remove file"
          aria-label={`Remove ${label}`}
        >
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>
      )}
    </span>
  )
}

/* ================================================================
   UPLOAD ZONE — opens file browser and uploads to backend
   ================================================================ */
function UploadZone({ label, isLocked, obligationId, onUploadComplete }) {
  const inputId = useId()
  const [uploading, setUploading] = useState(false)
  
  if (isLocked) return null
  
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !obligationId) return
    
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      
      const res = await authFetch(
        getLocalApiUrl(`/api/governance/obligations/${obligationId}/upload`),
        { method: 'POST', body: fd }
      )
      
      if (res.ok) {
        const json = await res.json()
        if (json.success && onUploadComplete) {
          onUploadComplete(obligationId, json.data)
        }
      } else {
        alert('Upload failed')
      }
    } catch (err) {
      console.error('Upload error:', err)
      alert('Upload failed')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }
  
  return (
    <label
      htmlFor={inputId}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-dashed border-gray-300 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:border-psg-teal hover:text-psg-teal hover:bg-psg-teal/5 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <input
        id={inputId}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={handleFileChange}
        disabled={uploading}
      />
      <ArrowUpTrayIcon className="w-3 h-3" />
      {uploading ? 'Uploading...' : (label || 'Upload PDF')}
    </label>
  )
}

// Routes that support ?year= for filtering (fiscal year start → calendar year)
const YEAR_AWARE_ROUTES = ['SMR Register', 'AUSTRAC Reporting', 'KYC Registry', 'ECDD Register']

// Convert fiscal year id (e.g. '2024-25') to calendar year for filter
function fiscalYearToCalendarYear(fiscalId) {
  if (!fiscalId || typeof fiscalId !== 'string') return ''
  const match = fiscalId.match(/^(\d{4})/)
  return match ? match[1] : ''
}

/* ================================================================
   DIGITAL LINK — navigates to existing app page (with year when applicable)
   ================================================================ */
function DigitalLink({ target, summary, navigate, selectedYear }) {
  const route = DIGITAL_ROUTES[target.page]
  const yearParam = YEAR_AWARE_ROUTES.includes(target.page) ? fiscalYearToCalendarYear(selectedYear) : ''
  const href = route && yearParam ? `${route}?year=${yearParam}` : route
  const handleClick = () => href && navigate(href)

  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
      <CheckCircleIcon className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-green-800 dark:text-green-200 truncate">{summary}</div>
      </div>
      {route && (
        <button
          type="button"
          onClick={handleClick}
          className="flex items-center gap-1 px-2.5 py-1 rounded border border-green-300 dark:border-green-700 bg-white dark:bg-gray-800 text-green-700 dark:text-green-300 text-[11px] font-semibold hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
        >
          Open {target.page}
          <ArrowTopRightOnSquareIcon className="w-3 h-3" />
        </button>
      )}
    </div>
  )
}

/* ================================================================
   GLOBAL OBLIGATION ROW
   ================================================================ */
function GlobalObligationRow({ ob, isLocked, navigate, selectedYear, onUploadComplete, onRemoveFile }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="card mb-1.5">
      <div
        onClick={() => setExpanded(!expanded)}
        className={`flex items-center gap-2.5 p-3 cursor-pointer transition-colors rounded-lg ${expanded ? 'bg-gray-50 dark:bg-gray-800/50' : ''}`}
      >
        {expanded ? (
          <ChevronDownIcon className="w-4 h-4 text-psg-teal flex-shrink-0" />
        ) : (
          <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{ob.name}</div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400">{ob.ref}</div>
        </div>
        <span className="text-[11px] text-gray-500 dark:text-gray-400 mr-1 hidden sm:inline">{ob.frequency}</span>
        <StatusDot status={ob.status} />
      </div>

      {expanded && (
        <div className="pt-0 pb-3 px-3 pl-9 border-t border-gray-100 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mt-2 mb-3">{ob.desc}</div>

          {ob.statusNote && (
            <div className="text-xs text-violet-600 dark:text-violet-400 italic mb-2">{ob.statusNote}</div>
          )}

          {ob.digitalStatus?.active && ob.digitalTarget && (
            <div className="mb-2">
              <DigitalLink target={ob.digitalTarget} summary={ob.digitalStatus.summary} navigate={navigate} selectedYear={selectedYear} />
            </div>
          )}

          {ob.uploads?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {ob.uploads.map((f, i) => (
                <FileChip
                  key={`${ob.id}-${i}-${f.path || f.name || i}`}
                  file={f}
                  isLocked={isLocked}
                  obligationId={ob.id}
                  fileIndex={i}
                  selectedYear={selectedYear}
                  onRemove={() => onRemoveFile?.(ob.id, i)}
                />
              ))}
            </div>
          )}

          {/* Only show upload when digital is not being used */}
          {!ob.digitalStatus?.active && (ob.mode === 'upload' || ob.mode === 'both') && (
            <UploadZone label={`Upload PDF — ${ob.name}`} isLocked={isLocked} obligationId={ob.id} onUploadComplete={onUploadComplete} />
          )}
        </div>
      )}
    </div>
  )
}

/* ================================================================
   VENUE-SPECIFIC OBLIGATION ROW
   ================================================================ */
function VenueObligationRow({ ob, isLocked, navigate, venues, selectedYear, onUploadComplete, onRemoveFile }) {
  const [expanded, setExpanded] = useState(false)
  const overallStatus = getOverallVenueStatus(ob.venueData)

  return (
    <div className="card mb-1.5">
      <div
        onClick={() => setExpanded(!expanded)}
        className={`flex items-center gap-2.5 p-3 cursor-pointer transition-colors rounded-lg ${expanded ? 'bg-gray-50 dark:bg-gray-800/50' : ''}`}
      >
        {expanded ? (
          <ChevronDownIcon className="w-4 h-4 text-psg-teal flex-shrink-0" />
        ) : (
          <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{ob.name}</div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400">{ob.ref}</div>
        </div>
        <div className="flex gap-1 mr-2">
          {ob.venueData.map((vd) => {
            const venue = venues.find((v) => v.id === vd.venueId)
            const s = STATUS_MAP[vd.status]
            return (
              <span
                key={vd.venueId}
                title={venue?.name}
                className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${s.bg} ${s.tx}`}
              >
                {venue?.shortName || venue?.name?.split(' ')[0]}
              </span>
            )
          })}
        </div>
        <span className="text-[11px] text-gray-500 dark:text-gray-400 mr-1 hidden sm:inline">{ob.frequency}</span>
        <StatusDot status={overallStatus} />
      </div>

      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed p-3 pl-9">{ob.desc}</div>

          {ob.venueData.map((vd, vi) => {
            const venue = venues.find((v) => v.id === vd.venueId)
            return (
              <div
                key={vd.venueId}
                className={`p-3 pl-9 ${vi > 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''} ${vi % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-bold text-psg-teal px-2.5 py-0.5 rounded bg-psg-teal/10">
                    {venue?.name}
                  </span>
                  <StatusDot status={vd.status} />
                </div>

                {vd.digitalStatus?.active && ob.digitalTarget && (
                  <div className="mb-2">
                    <DigitalLink target={ob.digitalTarget} summary={vd.digitalStatus.summary} navigate={navigate} selectedYear={selectedYear} />
                  </div>
                )}

                {vd.uploads?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {vd.uploads.map((f, i) => (
                      <FileChip
                        key={`${ob.id}-${vd.venueId}-${i}-${f.path || f.name || i}`}
                        file={f}
                        isLocked={isLocked}
                        obligationId={ob.id}
                        fileIndex={i}
                        selectedYear={selectedYear}
                        venueId={vd.venueId}
                        onRemove={() => onRemoveFile?.(ob.id, i, vd.venueId)}
                      />
                    ))}
                  </div>
                )}

                {/* Only show upload when digital is not being used for this venue */}
                {!vd.digitalStatus?.active && (ob.mode === 'upload' || ob.mode === 'both') && (
                  <UploadZone label={`Upload PDF — ${venue?.name || 'Document'}`} isLocked={isLocked} obligationId={ob.id} onUploadComplete={onUploadComplete} />
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* ================================================================
   MAIN
   ================================================================ */
export default function RegulatoryObligations() {
  const navigate = useNavigate()
  const [selectedYear, setSelectedYear] = useState('2025-26')
  const [venues, setVenues] = useState([])
  const [obligations, setObligations] = useState({ global: [], venue: [] })

  useEffect(() => {
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'))
        const j = await r.json()
        if (j.success && Array.isArray(j.data) && j.data.length > 0) {
          setVenues(
            j.data.map((v) => ({
              id: v.venue_id,
              name: v.name,
              shortName: deriveShortName(v.name, v.code),
            }))
          )
        }
      } catch {
        setVenues([])
      }
    })()
  }, [])

  const loadObligations = useCallback(async (yearId) => {
    try {
      const r = await authFetch(getLocalApiUrl(`/api/governance/obligations?year=${yearId}`))
      const j = await r.json()
      if (j.success && j.data) {
        setObligations(j.data)
      }
    } catch {
      setObligations({ global: [], venue: [] })
    }
  }, [])

  useEffect(() => { loadObligations(selectedYear) }, [selectedYear, loadObligations])

  const handleUploadComplete = useCallback((obligationId, uploadData) => {
    // Refresh obligations after upload
    loadObligations(selectedYear)
  }, [selectedYear, loadObligations])

  const handleRemoveFile = useCallback(async (obligationId, fileIndex, venueId) => {
    const qs = new URLSearchParams()
    if (selectedYear) qs.set('year', selectedYear)
    if (venueId) qs.set('venueId', venueId)
    const res = await authFetch(
      getLocalApiUrl(
        `/api/governance/obligations/${encodeURIComponent(obligationId)}/files/${encodeURIComponent(String(fileIndex))}${qs.toString() ? `?${qs}` : ''}`
      ),
      { method: 'DELETE' }
    )
    const j = await res.json().catch(() => ({}))
    if (!res.ok || j.success === false) {
      throw new Error(j.error || `Remove failed (${res.status})`)
    }
    await loadObligations(selectedYear)
  }, [selectedYear, loadObligations])

  const year = YEARS.find((y) => y.id === selectedYear)
  const isLocked = year.status === 'locked'

  const allGlobal = obligations.global
  const allVenue = obligations.venue
  const globalDone = allGlobal.filter((o) => o.status === 'done').length
  const venueDone = allVenue.filter((o) => getOverallVenueStatus(o.venueData) === 'done').length
  const totalItems = allGlobal.length + allVenue.length
  const totalDone = globalDone + venueDone
  const pct = totalItems ? Math.round((totalDone / totalItems) * 100) : 0

  return (
    <div className="max-w-7xl mx-auto pb-10">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <ShieldCheckIcon className="w-6 h-6 text-psg-teal" />
          Regulatory Obligations
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Annual compliance checklist — AUSTRAC obligations for gaming venues with 16+ EGM entitlements
        </p>
      </div>

      {/* Year selector */}
      <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 mb-4">
        {YEARS.map((y, i) => {
          const active = selectedYear === y.id
          const isLive = y.status === 'active'
          return (
            <button
              key={y.id}
              type="button"
              onClick={() => setSelectedYear(y.id)}
              className={`flex-1 px-3 py-2.5 text-center transition-colors border-r last:border-r-0 border-gray-200 dark:border-gray-700 ${
                active ? (isLive ? 'bg-psg-teal text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100') : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="text-sm font-bold flex items-center justify-center gap-1.5">
                {y.label}
                {isLive && (
                  <span
                    className={`text-[8px] px-1.5 py-0.5 rounded font-bold uppercase ${
                      active ? 'bg-green-500/80' : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                  >
                    Active
                  </span>
                )}
                {y.status === 'locked' && <LockClosedIcon className="w-3 h-3" />}
              </div>
              <div className="text-[10px] mt-0.5 opacity-80">{y.period}</div>
            </button>
          )
        })}
      </div>

      {isLocked && (
        <div className="flex gap-2 items-center p-2.5 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400 mb-3">
          <LockClosedIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span>
            <strong>{year.label}</strong> is locked (read-only). Locked on {year.lockedDate}. Expand/collapse allowed; no editing.
          </span>
        </div>
      )}

      {/* Summary */}
      <div className="card flex justify-between items-center mb-4">
        <div className="text-sm text-gray-700 dark:text-gray-300">
          <strong>{totalDone}</strong> of <strong>{totalItems}</strong> obligations covered
        </div>
        <div className="w-48 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${totalDone === totalItems ? 'bg-green-500' : 'bg-amber-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className={`text-sm font-bold ${totalDone === totalItems ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
          {pct}%
        </div>
      </div>

      {/* Business-Wide */}
      <div className="mb-6">
        <h2 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 px-1">
          Business-Wide
        </h2>
        {obligations.global.map((ob) => (
          <GlobalObligationRow key={ob.id} ob={ob} isLocked={isLocked} navigate={navigate} selectedYear={selectedYear} onUploadComplete={handleUploadComplete} onRemoveFile={handleRemoveFile} />
        ))}
      </div>

      {/* Venue-Specific */}
      <div>
        <div className="flex justify-between items-center mb-2 px-1">
          <h2 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Venue-Specific
          </h2>
          <div className="flex gap-1 flex-wrap">
            {venues.map((v) => (
              <span
                key={v.id}
                className="text-[11px] px-2 py-0.5 rounded bg-psg-teal/10 text-psg-teal font-semibold"
                title={v.name}
              >
                {v.name}
              </span>
            ))}
          </div>
        </div>
        {obligations.venue.map((ob) => (
          <VenueObligationRow key={ob.id} ob={ob} isLocked={isLocked} navigate={navigate} venues={venues} selectedYear={selectedYear} onUploadComplete={handleUploadComplete} onRemoveFile={handleRemoveFile} />
        ))}
      </div>
    </div>
  )
}
